<template>
  <div
    class="g1-wall"
    :class="[layoutModeClass, providerTheme.wallClass, { 'g1-wall--blackout': !visible }]"
  >
    <template v-if="visible">
      <header class="topbar">
        <span class="logo">{{ providerTheme.logoText }}</span>
        <h1>{{ headerTitle }}</h1>
        <time class="clock" :datetime="clockIso">{{ clockLabel }}</time>
      </header>
      <div class="wrap">
        <div v-if="currentItem" class="story-stage">
          <Transition name="g1-story-fade">
            <article
              :key="storyTransitionKey"
              class="story-screen story-screen--fade-layer"
            >
              <section
                v-if="showHeroImage"
                class="story-media"
              >
                <figure
                  class="story-photo"
                  :class="{ 'story-photo--rendered': photoRendered }"
                  :style="photoDisplay.style"
                >
                  <img
                    ref="photoImgRef"
                    :src="photoSrc"
                    :width="photoDisplay.width"
                    :height="photoDisplay.height"
                    alt=""
                    loading="lazy"
                    decoding="async"
                    @error="onPhotoError"
                    @load="onPhotoLoad"
                  >
                </figure>
              </section>
              <section class="story-panel">
                <div class="story-content">
                  <h2 class="story-title">
                    {{ currentItem.title }}
                  </h2>
                  <RssQrCode :url="currentItem.link" />
                </div>
              </section>
            </article>
          </Transition>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import type { AspectRatio } from '@lucimark/shared/database/custom-types/aspect-ratio'
import type { TRssParsedItem } from '@lucimark/shared/models/rssDirectory'
import { isRssStoryPhotoRendered, readRssStoryPhotoNaturalSize, resolveRssStoryPhotoDisplay, rssFeedTagFromLabel, rssPhotoPlaceholder } from '@lucimark/shared/helpers/rssFeedDisplay'
import { getRssWallProviderTheme } from '@lucimark/shared/helpers/rssWallProviderTheme'
import { computed, nextTick, ref, toRefs, watch } from 'vue'
import RssQrCode from '@/components/rss/RssQrCode.vue'
import { useG1WallClock } from '@/composables/useG1WallClock'

const props = withDefaults(defineProps<{
  items: TRssParsedItem[]
  currentIndex: number
  feedLabel: string
  visible: boolean
  /** RSS directory provider id (g1, uol, …) for top-bar branding. */
  providerId?: string
  /** Matches app {@link RssDirectoryFramePreview} — drives `g1-wall--layout-*` portrait/landscape rules. */
  layoutAspectRatio?: AspectRatio
}>(), {
  providerId: 'g1'
})

const { items, currentIndex, feedLabel, visible, layoutAspectRatio, providerId } = toRefs(props)

const providerTheme = computed(() => getRssWallProviderTheme(providerId.value))

const layoutModeClass = computed(() =>
  layoutAspectRatio.value ? `g1-wall--layout-${layoutAspectRatio.value}` : undefined
)

const imageFailed = ref(false)
const photoRendered = ref(false)
const photoImgRef = ref<HTMLImageElement | null>(null)
const naturalPhotoSize = ref<{ width: number, height: number }>()

const currentItem = computed(() => items.value[currentIndex.value] ?? null)

const storyTransitionKey = computed(() => {
  const item = currentItem.value
  if (!item) {
    return 'empty'
  }
  return `${currentIndex.value}-${item.link}`
})

const headerTitle = computed(() => rssFeedTagFromLabel(feedLabel.value))

const { clockLabel, clockIso } = useG1WallClock(visible)

const showHeroImage = computed(() => {
  if (layoutAspectRatio.value != null) {
    return true
  }
  if (typeof window === 'undefined') {
    return true
  }
  return window.innerHeight >= 280
})

const photoSrc = computed(() => {
  if (imageFailed.value || !currentItem.value?.imageUrl) {
    return rssPhotoPlaceholder(providerId.value)
  }
  return currentItem.value.imageUrl
})

const photoDisplay = computed(() => resolveRssStoryPhotoDisplay({
  item: currentItem.value,
  usingPlaceholder: imageFailed.value || !currentItem.value?.imageUrl,
  naturalWidth: naturalPhotoSize.value?.width,
  naturalHeight: naturalPhotoSize.value?.height
}))

function syncStoryPhotoFromImg(img: HTMLImageElement | null | undefined) {
  if (!isRssStoryPhotoRendered(img)) {
    return
  }
  photoRendered.value = true
  const size = readRssStoryPhotoNaturalSize(img!)
  if (size) {
    naturalPhotoSize.value = size
  }
}

function onPhotoLoad(event: Event) {
  syncStoryPhotoFromImg(event.target as HTMLImageElement)
}

function onPhotoError() {
  imageFailed.value = true
  photoRendered.value = false
}

watch(currentItem, async () => {
  imageFailed.value = false
  naturalPhotoSize.value = undefined
  photoRendered.value = false
  await nextTick()
  syncStoryPhotoFromImg(photoImgRef.value)
})
</script>

<style scoped>
@import '@lucimark/shared/styles/g1-wall.css';

.g1-wall {
  font-family: var(--g1-font);
  background: var(--g1-bg);
  color: var(--g1-text);
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.g1-wall--blackout {
  background: #000;
}

.topbar {
  background: linear-gradient(90deg, var(--g1-red), var(--g1-red-dark));
  color: #fff;
}

.logo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  color: var(--g1-red);
  font-weight: 800;
  font-size: var(--g1-logo-size, clamp(28px, 3vw, 44px));
  line-height: 1;
  padding: 2px 14px;
  border-radius: 8px;
  flex-shrink: 0;
}

.story-panel {
  min-height: 0;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.story-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: 100%;
}

.story-title {
  margin: 0;
  font-size: var(--g1-story-title-size, clamp(26px, 3vw, 52px));
  font-weight: 700;
  line-height: 1.25;
  max-width: 42ch;
  color: var(--g1-text);
}
</style>
